import React, { useCallback, useContext, useRef } from 'react'
import { useCookies } from 'react-cookie'
import { withRouter } from 'react-router'

import { useEditor } from '@toasttab/sites-components'
import crc from 'crc-32'
import { v4 as uuid } from 'uuid'

import { RequestContext, RequestContextProps } from 'src/lib/js/context'
import {
  isToastLocalRequest,
  isToastOrderRequest
} from 'src/public/js/siteUtilities'

import { useFlags } from '../feature_flags/useFlags'
import { TESTS } from './tests.config'
import { ABTest, Tests, TestSurface } from './types'

export const COOKIE_NAME = 'toast-sites-experiment-id'
const getOverrideCookieName = (experimentName: string): string =>
  `toast-sites-experiment-override|${experimentName}`
const canRunTestOnSurface = (
  staticContext: RequestContext | undefined,
  surfaces: TestSurface[]
): boolean => {
  if (isToastLocalRequest(staticContext)) {
    return surfaces.includes(TestSurface.TOAST_LOCAL)
  } else if (isToastOrderRequest(staticContext)) {
    return surfaces.includes(TestSurface.OOV4)
  }

  return surfaces.includes(TestSurface.CUSTOM_DOMAINS)
}

/**
 * Applies LaunchDarkly dynamic percentage control to variant distribution.
 *
 * If a test has a LaunchDarkly flag configured, this function uses the flag's numeric
 * value (0-100) to dynamically control the traffic split between control and test variants.
 *
 * How it works:
 * - The LD flag value represents the percentage of users who should see non-control variants
 * - Control variant gets (100 - flagValue)%
 * - Non-control variants split the flagValue% equally among themselves
 *
 * Examples:
 * - Flag value = 50, variants = {control, test}
 *   → control: 50%, test: 50%
 *
 * - Flag value = 25, variants = {control, variantA, variantB}
 *   → control: 75%, variantA: 12.5%, variantB: 12.5%
 *
 * - Flag value = 0 → 100% control (test disabled)
 * - Flag value = 100 → 0% control, 100% split among test variants
 *
 * This allows real-time traffic control without code deploys.
 *
 * @param test - The A/B test configuration
 * @param launchDarklyFlags - Current LaunchDarkly flag values
 * @returns Variant distribution with LaunchDarkly overrides applied, or null if no override
 */
const applyLaunchDarklyPercentageOverride = (
  test: ABTest,
  launchDarklyFlags?: Record<string, any>
): { [key: string]: number } | null => {
  if (!test.launchDarklyFlag || !launchDarklyFlags) {
    return null
  }

  const flagValue = launchDarklyFlags[test.launchDarklyFlag]

  if (typeof flagValue !== 'number') {
    return null
  }

  // Clamp the flag value between 0 and 100
  const testPercentage = Math.max(0, Math.min(100, flagValue))
  const controlPercentage = 100 - testPercentage

  // Get all non-control variants
  const variantNames = Object.keys(test.variants)
  const nonControlVariants = variantNames.filter(
    (v) => v !== test.controlVariant
  )

  // Build new percentage distribution
  const newVariants: { [key: string]: number } = {
    [test.controlVariant]: controlPercentage
  }

  // Split test percentage equally among all non-control variants
  if (nonControlVariants.length > 0) {
    const percentagePerVariant = testPercentage / nonControlVariants.length
    nonControlVariants.forEach((variantName) => {
      newVariants[variantName] = percentagePerVariant
    })
  }

  return newVariants
}

type ExperimentContextType = {
  getSelectedVariant: (experimentName: string) => string
}

const ExperimentContext = React.createContext<
  ExperimentContextType | undefined
>(undefined)

type ProviderProps = {
  onVariantSelected?: (experimentName: string, variantName: string) => void
  tests?: Tests
}

export const getSelectedVariant = ({
  experimentName,
  userId,
  cookies,
  onVariantSelected,
  staticContext,
  tests = TESTS,
  launchDarklyFlags
}: {
  experimentName: string
  cookies: { [key: string]: string }
  userId: string
  onVariantSelected?: (experimentName: string, variantName: string) => void
  staticContext?: RequestContext
  tests?: Tests
  launchDarklyFlags?: Record<string, any>
}): string => {
  const overrideCookieName = getOverrideCookieName(experimentName as string)
  const overrideCookie = cookies[overrideCookieName]
  if (overrideCookie) {
    // If we have a test overridden, use that as our selected variant
    onVariantSelected?.(experimentName, overrideCookie)
    return overrideCookie
  }

  const test = tests[experimentName]
  let variants: { [key: string]: number } = test?.variants || {}

  if (!test || !test.variants) {
    throw new Error('Attempted to render a test that does not exist')
  }

  if (
    test.disabled ||
    !canRunTestOnSurface(staticContext, test.testSurfaces) ||
    staticContext?.isBot
  ) {
    return test.controlVariant
  }

  // Apply LaunchDarkly dynamic percentage override if configured
  const ldOverride = applyLaunchDarklyPercentageOverride(
    test,
    launchDarklyFlags
  )
  if (ldOverride) {
    variants = ldOverride
  }

  /*
   * For a set of variants { a: 20, b: 50, c: 30 }
   * we create lists [a, b, c] and [20, 50, 30].
   * Then we can create a running sum [20, 70, 100],
   * and return the variant at the first index where
   * the normalized userId is less than that running sum.
   */
  const variantNames = Object.keys(variants).sort() // sort for determinism
  const variantWeights = variantNames.map(
    (name) => variants[name as keyof typeof variants]
  ) as number[]
  const runningSum = variantWeights.reduce((sum, val, index) => {
    const prevWeight = sum[index - 1]
    return [...sum, prevWeight ? prevWeight + val : val]
  }, [])
  const sum = variantWeights.reduce((acc, curr) => acc + curr, 0)

  // Adding experiment name here so that when multiple experiments are set at 50/50 it's not the same group of users in each bucket
  const weightedIndex = Math.abs(crc.str(`${userId}:${experimentName}`)) % sum
  const variantIndex = runningSum.findIndex((weight) => weightedIndex < weight)
  const selectedVariant = variantNames[variantIndex] || test.controlVariant

  onVariantSelected?.(experimentName, selectedVariant)

  return selectedVariant
}

/**
 * This cookie is being stripped for specific faceboookexternalhit user agent requests directly tied to social media previews
 * and should have no negative impact on experiment tracking. However, if it does have impact, you can reference
 * the cloudflare cache rule here: https://toast.sourcegraph.com/r/github.toasttab.com/toasttab/cloud-config-network/-/blob/cloudflare/prod/sites-toasttab-com/all_cache_rules.tf
 */
export const useExperimentUserId = (): string => {
  const [cookies] = useCookies([COOKIE_NAME])
  return cookies[COOKIE_NAME]
}

export const getExperimentUserId = (cookies: {
  [key: string]: string
}): string | undefined => {
  return cookies[COOKIE_NAME]
}

const WrappedExperimentContextProvider = ({
  children,
  onVariantSelected: onVariantSelectedProp,
  tests = TESTS,
  staticContext
}: React.PropsWithChildren<ProviderProps & RequestContextProps>) => {
  const [cookies, setCookie] = useCookies([
    COOKIE_NAME,
    ...Object.keys(tests).map(getOverrideCookieName)
  ])
  const selectedVariantRef = useRef<{ [key: string]: string }>({})

  // Get LaunchDarkly flags for dynamic percentage control
  const launchDarklyFlags = useFlags()

  const onVariantSelected = useCallback(
    (experimentName: string, variantName: string) => {
      selectedVariantRef.current[experimentName] = variantName
      if (onVariantSelectedProp) {
        onVariantSelectedProp(experimentName, variantName)
      }
    },
    [onVariantSelectedProp]
  )

  let userId = useExperimentUserId()
  if (!userId) {
    userId = uuid()
    setCookie(COOKIE_NAME, userId, {
      expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    })
  }

  const getVariant = useCallback(
    (experimentName: string): string => {
      const cachedSelectedVariant = selectedVariantRef.current[experimentName]
      if (cachedSelectedVariant) {
        // We've already selected a variant, just return it
        return cachedSelectedVariant
      }

      return getSelectedVariant({
        cookies,
        onVariantSelected,
        userId,
        staticContext,
        experimentName,
        tests,
        launchDarklyFlags
      })
    },
    [
      cookies,
      onVariantSelected,
      userId,
      staticContext,
      tests,
      launchDarklyFlags
    ]
  )

  return (
    <ExperimentContext.Provider value={{ getSelectedVariant: getVariant }}>
      {children}
    </ExperimentContext.Provider>
  )
}

const RoutedExperimentContextProvider = withRouter<
  ProviderProps & RequestContextProps,
  React.ComponentType<ProviderProps & RequestContextProps>
>(WrappedExperimentContextProvider)

export const ExperimentContextProvider = ({
  tests = TESTS,
  ...props
}: React.PropsWithChildren<ProviderProps>) => {
  const { isEditor } = useEditor()

  // Singleton enforcement: Check if provider already exists in tree
  const parentProvider = useContext(ExperimentContext)

  if (parentProvider) {
    const error = new Error(
      '[ExperimentContextProvider] Duplicate ExperimentContextProvider detected. ' +
        'Only one ExperimentContextProvider should exist in the component tree. '
    )
    throw error
  }

  if (isEditor) {
    return (
      <ExperimentContext.Provider
        value={{
          getSelectedVariant: (experimentName) => {
            const test = tests[experimentName]

            if (!test) {
              throw new Error('Attempted to render a test that does not exist')
            }

            return test.controlVariant
          }
        }}
      >
        {props.children}
      </ExperimentContext.Provider>
    )
  }

  return <RoutedExperimentContextProvider {...props} tests={tests} />
}

export const useExperiment = (experimentName: string, tests: Tests = TESTS) => {
  const context = useContext(ExperimentContext)
  return {
    selectedVariant: context
      ? context.getSelectedVariant(experimentName)
      : tests[experimentName]?.controlVariant
  }
}

/**
 * Hook to get variant calculation function for on-demand use (e.g., in event handlers).
 * Use this when you need to calculate the variant lazily to avoid SSR/hydration issues.
 *
 * @returns A function that takes an experiment name and returns the variant
 *
 * @example
 * const getVariant = useVariantCalculator()
 *
 * const onClick = () => {
 *   const variant = getVariant('my-experiment')
 *   if (variant === 'test') {
 *     console.log('User is in test group')
 *   }
 * }
 */
export const useVariantCalculator = () => {
  const [cookies] = useCookies([COOKIE_NAME])
  const launchDarklyFlags = useFlags()

  return useCallback(
    (experimentName: string): string | undefined => {
      const userId = getExperimentUserId(cookies)
      if (!userId) {
        return undefined
      }

      return getSelectedVariant({
        experimentName,
        userId,
        cookies,
        launchDarklyFlags
      })
    },
    [cookies, launchDarklyFlags]
  )
}
